/**
 * @fileoverview SuperAdminHealth — super-admin System → Health dashboard.
 *
 * Two columns:
 *   Left  — containers listing (name, status, uptime, memory, restart count).
 *           Data comes from the local Docker daemon via a read-only socket
 *           mount into the backend container (routes/admin_health.py).
 *   Right — AWS managed-service state: RDS, ElastiCache, S3, SES, SQS.
 *           Each block is fail-open — a per-service `error` string renders
 *           inline without breaking the rest.
 *
 * Auto-refreshes every 10 s and exposes a manual "Refresh now" button that
 * shows the last-refreshed timestamp. Wrapped in Seedling design tokens —
 * no inline styles, no hex literals.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

const AUTO_REFRESH_MS = 10000;

/**
 * Bucket a container's `state` string into one of the three Seedling badge
 * variants so the UI is consistent regardless of Docker's exact vocabulary.
 *
 * @param {string} state - Docker state (`running`, `exited`, `restarting`, ...)
 * @param {?string} health - HEALTHCHECK status if the container has one
 * @returns {'success'|'warning'|'danger'} badge variant
 */
function containerBadgeVariant(state, health) {
  if (state === 'running') {
    if (health === 'unhealthy') return 'danger';
    if (health === 'starting') return 'warning';
    return 'success';
  }
  if (state === 'restarting' || state === 'created') return 'warning';
  return 'danger';
}

/**
 * Same bucketing for AWS statuses. `available`/`ok` is success; anything
 * that looks in-flight (`creating`, `modifying`, `backing-up`) is warning;
 * anything else — including `stopped`, `failed`, `deleting` — is danger.
 */
function awsBadgeVariant(status) {
  if (!status) return 'warning';
  const s = String(status).toLowerCase();
  if (s === 'available' || s === 'ok') return 'success';
  if (s.includes('creating') || s.includes('modifying') || s.includes('backing-up')) {
    return 'warning';
  }
  return 'danger';
}

/**
 * Render an integer byte count as a human-readable string.
 *
 * @param {?number} n - bytes
 * @returns {string}
 */
function fmtBytes(n) {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB'];
  let v = n / 1024;
  let idx = 0;
  while (v >= 1024 && idx < units.length - 1) {
    v /= 1024;
    idx += 1;
  }
  return `${v.toFixed(1)} ${units[idx]}`;
}

/**
 * Render an uptime in seconds as `1d 03h 12m` (drops trailing zeros).
 */
function fmtUptime(sec) {
  if (sec === null || sec === undefined) return '—';
  const s = Math.max(0, Math.floor(sec));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days}d ${String(hours).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m`;
  if (hours > 0) return `${hours}h ${String(mins).padStart(2, '0')}m`;
  return `${mins}m`;
}

/**
 * Colored status pill using the seedling badge classes.
 */
function StatusBadge({ variant, children }) {
  return <span className={`badge badge-${variant}`}>{children}</span>;
}

/**
 * A collapsible AWS service section with a header status dot + count.
 */
function AwsSection({ id, title, defaultOpen, headerVariant, headerRight, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const panelId = `admin-health-aws-${id}`;
  return (
    <section className="admin-health__aws-section" data-testid={`aws-section-${id}`}>
      <button
        type="button"
        className="admin-health__aws-header"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`admin-health__dot admin-health__dot--${headerVariant}`} aria-hidden="true" />
        <span className="admin-health__aws-title">{title}</span>
        {headerRight ? <span className="admin-health__aws-meta">{headerRight}</span> : null}
        <svg
          className={`admin-health__chevron admin-health__chevron--${open ? 'open' : 'closed'}`}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div id={panelId} className="admin-health__aws-body" hidden={!open}>
        {children}
      </div>
    </section>
  );
}

/**
 * Small labelled key / value block used inside the AWS body.
 */
function Metric({ label, value, mono }) {
  return (
    <div className="admin-health__metric">
      <span className="admin-health__metric-label">{label}</span>
      <span className={`admin-health__metric-value${mono ? ' admin-health__mono' : ''}`}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </span>
    </div>
  );
}

/**
 * RDS AWS block.
 */
function RdsBlock({ data }) {
  if (data && data.error) {
    return <p className="admin-health__error" role="alert">RDS: {data.error}</p>;
  }
  const instances = (data && data.instances) || [];
  if (instances.length === 0) {
    return <p className="admin-health__empty">No DB instances visible to this role.</p>;
  }
  return (
    <ul className="admin-health__aws-list">
      {instances.map((db) => (
        <li key={db.id} className="admin-health__aws-item">
          <div className="admin-health__aws-item-title">
            <StatusBadge variant={awsBadgeVariant(db.status)}>{db.status || 'unknown'}</StatusBadge>
            <span className="admin-health__mono">{db.id}</span>
          </div>
          <div className="admin-health__aws-item-meta">
            {db.engine} {db.engine_version} · {db.class}
            {db.multi_az ? ' · Multi-AZ' : ''}
            {db.storage_gb ? ` · ${db.storage_gb} GB` : ''}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ElastiCacheBlock({ data }) {
  if (data && data.error) {
    return <p className="admin-health__error" role="alert">ElastiCache: {data.error}</p>;
  }
  const clusters = (data && data.clusters) || [];
  if (clusters.length === 0) {
    return <p className="admin-health__empty">No cache clusters visible to this role.</p>;
  }
  return (
    <ul className="admin-health__aws-list">
      {clusters.map((c) => (
        <li key={c.id} className="admin-health__aws-item">
          <div className="admin-health__aws-item-title">
            <StatusBadge variant={awsBadgeVariant(c.status)}>{c.status || 'unknown'}</StatusBadge>
            <span className="admin-health__mono">{c.id}</span>
          </div>
          <div className="admin-health__aws-item-meta">
            {c.engine} {c.engine_version} · {c.node_type} · {c.num_nodes} node(s)
          </div>
        </li>
      ))}
    </ul>
  );
}

function S3Block({ data }) {
  if (data && data.error) {
    return <p className="admin-health__error" role="alert">S3: {data.error}</p>;
  }
  const buckets = (data && data.buckets) || [];
  if (buckets.length === 0) {
    return <p className="admin-health__empty">No cogslog-* buckets found.</p>;
  }
  return (
    <ul className="admin-health__aws-list">
      {buckets.map((b) => (
        <li key={b.name} className="admin-health__aws-item">
          <div className="admin-health__aws-item-title">
            <StatusBadge variant="success">ok</StatusBadge>
            <span className="admin-health__mono">{b.name}</span>
          </div>
          {b.created_at ? (
            <div className="admin-health__aws-item-meta">created {b.created_at}</div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function SesBlock({ data }) {
  if (data && data.error) {
    return <p className="admin-health__error" role="alert">SES: {data.error}</p>;
  }
  if (!data) return null;
  const stats = data.stats_24h || {};
  return (
    <div className="admin-health__metrics">
      <Metric label="Sent (last 24h)" value={data.sent_last_24h} mono />
      <Metric label="24h quota" value={data.max_24_hour_send} mono />
      <Metric label="Max send rate/sec" value={data.max_send_rate} mono />
      <Metric label="Deliveries (24h)" value={stats.delivery} mono />
      <Metric label="Bounces (24h)" value={stats.bounces} mono />
      <Metric label="Complaints (24h)" value={stats.complaints} mono />
      <Metric label="Rejects (24h)" value={stats.rejects} mono />
    </div>
  );
}

function SqsBlock({ data }) {
  if (data && data.error) {
    return <p className="admin-health__error" role="alert">SQS: {data.error}</p>;
  }
  const queues = (data && data.queues) || [];
  if (queues.length === 0) {
    return <p className="admin-health__empty">No cogslog-* queues found.</p>;
  }
  return (
    <ul className="admin-health__aws-list">
      {queues.map((q) => (
        <li key={q.name} className="admin-health__aws-item">
          <div className="admin-health__aws-item-title">
            <StatusBadge variant={q.error ? 'danger' : 'success'}>
              {q.error ? 'error' : 'ok'}
            </StatusBadge>
            <span className="admin-health__mono">{q.name}</span>
          </div>
          {q.error ? (
            <div className="admin-health__aws-item-meta">{q.error}</div>
          ) : (
            <div className="admin-health__aws-item-meta admin-health__mono">
              available {q.messages_available} · in-flight {q.messages_in_flight} · delayed {q.messages_delayed}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Compute a single header status pill for an AWS section from its payload.
 */
function awsHeaderVariant(sub, statusesGetter) {
  if (!sub || sub.error) return 'danger';
  const statuses = statusesGetter(sub);
  if (!statuses || statuses.length === 0) return 'warning';
  const variants = statuses.map((s) => awsBadgeVariant(s));
  if (variants.some((v) => v === 'danger')) return 'danger';
  if (variants.some((v) => v === 'warning')) return 'warning';
  return 'success';
}

/**
 * Super-admin System → Health dashboard page.
 *
 * @param {Object} props
 * @param {string} props.adminApiPrefix - Full admin API prefix.
 * @returns {JSX.Element}
 */
function SuperAdminHealth({ adminApiPrefix }) {
  const [containers, setContainers] = useState(null);
  const [containersErr, setContainersErr] = useState(null);
  const [aws, setAws] = useState(null);
  const [awsErr, setAwsErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!adminApiPrefix) return;
    // Flip loading true on EVERY refresh (not just the initial mount) so
    // the Refresh now button changes to "Refreshing…" + disables, and the
    // timestamp announces "Loading…" via role="status". Bug caught in UAT
    // 2026-08-29: without this, clicking Refresh looked like a no-op —
    // the async work ran silently in the background.
    if (mountedRef.current) setLoading(true);
    try {
      const [cRes, aRes] = await Promise.all([
        api.getAdminHealthContainers(adminApiPrefix).catch((e) => ({ __err: e })),
        api.getAdminHealthAws(adminApiPrefix).catch((e) => ({ __err: e })),
      ]);
      if (!mountedRef.current) return;

      if (cRes && cRes.__err) {
        setContainersErr('Could not load container list.');
      } else {
        setContainers(cRes.data || null);
        setContainersErr(null);
      }

      if (aRes && aRes.__err) {
        setAwsErr('Could not load AWS status.');
      } else {
        setAws(aRes.data || null);
        setAwsErr(null);
      }

      setRefreshedAt(new Date());
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [adminApiPrefix]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const id = setInterval(refresh, AUTO_REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [refresh]);

  const containerList = (containers && containers.containers) || [];
  const containerPayloadErr = (containers && containers.error) || null;

  return (
    <div className="admin-health" data-testid="admin-health-panel">
      <header className="admin-health__header">
        <div>
          <h2>System health</h2>
          <p className="admin-health__blurb">
            Live view of every service in this stack — containers on the left,
            AWS managed dependencies on the right. Auto-refreshes every 10
            seconds.
          </p>
        </div>
        <div className="admin-health__toolbar">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={refresh}
            data-testid="admin-health-refresh"
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh now'}
          </button>
          <span
            className="admin-health__refreshed"
            role="status"
            data-testid="admin-health-refreshed"
          >
            {refreshedAt
              ? `Updated ${refreshedAt.toLocaleTimeString()}`
              : loading
                ? 'Loading…'
                : 'Not yet refreshed'}
          </span>
        </div>
      </header>

      <div className="admin-health__columns">
        {/* --- Containers --- */}
        <section className="admin-health__column card" aria-labelledby="admin-health-containers-title">
          <h3 id="admin-health-containers-title">Containers</h3>
          {containersErr ? (
            <p className="admin-health__error" role="alert">{containersErr}</p>
          ) : null}
          {containerPayloadErr ? (
            <p className="admin-health__error" role="alert">Docker: {containerPayloadErr}</p>
          ) : null}
          <div
            className="tbl-wrap"
            aria-live="polite"
            data-testid="admin-health-containers-table"
          >
            <table>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Status</th>
                  <th scope="col">Uptime</th>
                  <th scope="col">Memory</th>
                  <th scope="col">Restarts</th>
                </tr>
              </thead>
              <tbody>
                {containerList.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="admin-health__empty-row">
                      {loading ? 'Loading containers…' : 'No containers reported.'}
                    </td>
                  </tr>
                ) : (
                  containerList.map((c) => (
                    <tr key={c.name} data-testid={`container-row-${c.name}`}>
                      <td>
                        <div className="admin-health__mono">{c.name}</div>
                        <div className="admin-health__cell-meta">{c.image || ''}</div>
                      </td>
                      <td>
                        <StatusBadge variant={containerBadgeVariant(c.state, c.health_status)}>
                          {c.health_status ? `${c.state} · ${c.health_status}` : (c.state || c.status || 'unknown')}
                        </StatusBadge>
                      </td>
                      <td className="admin-health__mono">{fmtUptime(c.uptime_seconds)}</td>
                      <td className="admin-health__mono">{fmtBytes(c.memory_bytes)}</td>
                      <td className="admin-health__mono">{c.restart_count ?? 0}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* --- AWS --- */}
        <section className="admin-health__column card" aria-labelledby="admin-health-aws-title">
          <h3 id="admin-health-aws-title">
            AWS managed services{aws && aws.region ? ` · ${aws.region}` : ''}
          </h3>
          {awsErr ? (
            <p className="admin-health__error" role="alert">{awsErr}</p>
          ) : null}
          {!aws && !awsErr ? (
            <p className="admin-health__empty">
              {loading ? 'Loading AWS status…' : 'No AWS data.'}
            </p>
          ) : null}
          {aws ? (
            <div className="admin-health__aws-sections">
              <AwsSection
                id="rds"
                title="RDS"
                defaultOpen
                headerVariant={awsHeaderVariant(aws.rds, (d) => (d.instances || []).map((i) => i.status))}
                headerRight={aws.rds && !aws.rds.error ? `${(aws.rds.instances || []).length} instance(s)` : null}
              >
                <RdsBlock data={aws.rds} />
              </AwsSection>
              <AwsSection
                id="elasticache"
                title="ElastiCache"
                defaultOpen
                headerVariant={awsHeaderVariant(aws.elasticache, (d) => (d.clusters || []).map((c) => c.status))}
                headerRight={aws.elasticache && !aws.elasticache.error ? `${(aws.elasticache.clusters || []).length} cluster(s)` : null}
              >
                <ElastiCacheBlock data={aws.elasticache} />
              </AwsSection>
              <AwsSection
                id="s3"
                title="S3"
                headerVariant={awsHeaderVariant(aws.s3, (d) => (d.buckets || []).map(() => 'available'))}
                headerRight={aws.s3 && !aws.s3.error ? `${(aws.s3.buckets || []).length} bucket(s)` : null}
              >
                <S3Block data={aws.s3} />
              </AwsSection>
              <AwsSection
                id="ses"
                title="SES"
                headerVariant={aws.ses && !aws.ses.error ? 'success' : 'danger'}
                headerRight={aws.ses && !aws.ses.error && aws.ses.sent_last_24h != null
                  ? `${aws.ses.sent_last_24h}/${aws.ses.max_24_hour_send} sent 24h`
                  : null}
              >
                <SesBlock data={aws.ses} />
              </AwsSection>
              <AwsSection
                id="sqs"
                title="SQS"
                headerVariant={awsHeaderVariant(aws.sqs, (d) => (d.queues || []).map((q) => q.error ? 'failed' : 'available'))}
                headerRight={aws.sqs && !aws.sqs.error ? `${(aws.sqs.queues || []).length} queue(s)` : null}
              >
                <SqsBlock data={aws.sqs} />
              </AwsSection>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

export default SuperAdminHealth;
